/* Gestion du consentement cookies (conforme CNIL).
 *
 * Trois catégories : "essential" (toujours actif, pas de consentement requis),
 * "analytics" (Firebase Analytics / GA4, consentement explicite), "marketing"
 * (réservé pour l'avenir, aucun cookie déposé aujourd'hui).
 *
 * Stockage localStorage sous clé versionnée ("arpentia_consent_v1") — tout
 * changement de structure doit bumper la version ET vider l'ancien état pour
 * re-solliciter l'utilisateur. Expiration 6 mois (recommandation CNIL 13 mois
 * max, on choisit plus court pour redemander plus souvent).
 *
 * Souscription aux changements via onConsentChange — utilisée par firebase.ts
 * pour ne charger getAnalytics() qu'après accord explicite. Synchro
 * multi-onglets gérée via l'événement "storage" natif.
 */

export type ConsentCategory = "essential" | "analytics" | "marketing";

export type ConsentState = {
  version: number;
  timestamp: number;
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentChoice = {
  analytics?: boolean;
  marketing?: boolean;
};

const STORAGE_KEY = "arpentia_consent_v1";
const CURRENT_VERSION = 1;
// 6 mois — au-delà, needsConsent() renvoie true et la bannière réapparaît.
const MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000;

type Listener = (state: ConsentState | null) => void;
const listeners = new Set<Listener>();

function readRaw(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CURRENT_VERSION) return null;
    if (Date.now() - parsed.timestamp > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentState | null {
  return readRaw();
}

export function needsConsent(): boolean {
  return readRaw() === null;
}

export function hasConsent(category: ConsentCategory): boolean {
  if (category === "essential") return true;
  return readRaw()?.[category] === true;
}

export function setConsent(choice: ConsentChoice): void {
  const state: ConsentState = {
    version: CURRENT_VERSION,
    timestamp: Date.now(),
    essential: true,
    analytics: choice.analytics ?? false,
    marketing: choice.marketing ?? false,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota plein ou stockage désactivé — on laisse passer silencieusement,
    // la bannière réapparaîtra à la prochaine visite.
  }
  for (const l of listeners) l(state);
}

export function acceptAll(): void {
  setConsent({ analytics: true, marketing: true });
}

export function rejectAll(): void {
  setConsent({ analytics: false, marketing: false });
}

/** Efface le choix stocké — utile pour debug ou pour forcer une
 *  re-sollicitation après changement de politique. */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
  for (const l of listeners) l(null);
}

export function onConsentChange(listener: Listener): () => void {
  listeners.add(listener);
  // Synchro multi-onglets : si l'utilisateur change son choix dans un autre
  // onglet, on propage ici aussi (sinon Analytics resterait chargé/déchargé
  // de façon incohérente entre onglets).
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener(readRaw());
  };
  window.addEventListener("storage", storageHandler);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", storageHandler);
  };
}

/** Événement custom pour ouvrir la bannière en mode "Personnaliser" depuis
 *  un lien "Gérer mes cookies" n'importe où dans l'app. */
export const OPEN_COOKIE_SETTINGS_EVENT = "arpentia:open-cookie-settings";

export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
